/*
 * File: ContactsColumn.tsx
 * Project: a-a
 * File Created: Thursday, 16th April 2026 9:04:30 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Thursday, 16th April 2026 9:14:16 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2026 Mutual Mobile, Mutual Mobile
 */
import { Box, Skeleton, Typography } from '@mui/material';
import ContactCard from 'components/featured/Contacts/ContactsGrid/ContactCard/ContactCard';
import { Contact } from 'model/contacts.model';
import { useEffect, useState } from 'react';
import { ContactService } from 'service/contact.service';
import zeplinColor from 'theme/Color';

const ContactsColumn = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const res = await ContactService.getAllContacts(true, {
        page: 1,
        limit: 10,
      });
      setContacts(res?.contacts ?? res?.data ?? []);
    } catch (err) {
      console.error('Error fetching contacts', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleDelete = () => {
    fetchContacts();
  };

  const handleEdit = (contact: Contact) => {
    // TODO: open edit drawer
    console.log('Edit contact:', contact);
  };

  const handleViewDetails = (contact: Contact) => {
    // TODO: open view drawer
    console.log('View contact:', contact);
  };

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: '14px',
        border: `1px solid ${zeplinColor.Neutral5}`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: `1px solid ${zeplinColor.Neutral5}`,
        }}
      >
        <Typography variant='responsiveHeading3'>
          Contacts {!loading && `(${contacts.length})`}
        </Typography>
        <Typography variant='responsiveButtonSmall' sx={{ cursor: 'pointer' }}>
          See All
        </Typography>
      </Box>

      {/* Scrollable contact list */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          maxHeight: { xs: 'calc(100vh - 580px)', xl: 'calc(100vh - 680px)' },
        }}
      >
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Box key={i} sx={{ padding: '16px' }}>
              <Skeleton variant='rounded' height={120} sx={{ borderRadius: '14px' }} />
            </Box>
          ))
        ) : contacts.length > 0 ? (
          contacts.map((contact) => (
            <Box
              key={contact.contactId}
              sx={{
                padding: '12px 16px',
              }}
            >
              <ContactCard
                contact={contact}
                onViewDetails={handleViewDetails}
                onDelete={handleDelete}
                onEdit={handleEdit}
              />
            </Box>
          ))
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              padding: '24px',
            }}
          >
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
              No contacts found
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ContactsColumn;
